import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import Details, { detailsValidationSchema } from "./Component/Details";
import Controllers, { controlsValidationSchema } from "./Component/Controllers";
import HistoryStep from "./Component/HistoryStep";
import Attachment from "./Component/Attachment";
import Formula, { formulaValidationSchema } from "./Component/Formula";
import Wizard from "./Wizard";
import api from "../../providers/api";
import {
  UnitOfMeasureList,
  findUnitLabel,
  findObjBYSource,
  cleanKPIMeasures,
  addToast,
} from "../../helpers";
import "./style.scss";
import moment from "moment";

const generateFormulaRegex = (parameters = []) =>
  new RegExp(
    `\\+|-|\\*|\\/|100%${parameters.reduce(
      (acc, item) => `${acc}|${item.ParameterName}`,
      ""
    )}`,
    "g"
  );

function KPIWizard(props) {
  const intl = useIntl();
  const [
    strategyTypes,
    objectiveLevels,
    polarities,
    kpiSources,
    calculationMethods,
    frequencies,
    user,
  ] = useSelector(({ theme, user }) => [
    theme.StrategyTypes.item.Items,
    theme?.ObjectiveLevel?.item?.Items,
    theme.Polarities.item.Items,
    theme.KPISource.item.Items.filter((_item) => _item.Value !== "auto"),
    theme.CalculationMethods.item.Items,
    theme.Frequencies.item.Items,
    user.user,
  ]);
  const formRef = useRef();
  const [hasParameter, setHasParameter] = useState(false);
  const [hasNoTarget, setHasNoTarget] = useState(false);
  const [hasNoBaseline, setHasNoBaseline] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isOrderValid, setIsOrderValid] = useState(
    props?.modeSaveOrUpdate ? false : true
  );
  const [isCodeValid, setIsCodeValid] = useState(
    props?.modeSaveOrUpdate ? false : true
  );
  const [historyLog, setHistoryLog] = useState([]);
  const [approvalSteps, setApprovalSteps] = useState([]);
  const [yearsValue, setYearsValue] = useState(1);

  const [lists, setLists] = useState({
    kpiTypes: [],
    divisions: [],
    users: [],
    sectors: [],
    strategicObjectives: [],
    strategiesWithThemes: [],
    strategiesWithPillars: [],
    loading: {
      kpiTypes: true,
      divisions: true,
      users: true,
      strategicObjectives: true,
      strategiesWithThemes: true,
      strategiesWithPillars: true,
    },
  });
  const [state, setState] = useState({
    EnglishDescription: "",
    ArabicDescription: "",
    EnglishEquation: "",
    ArabicEquation: "",
    Polarity: polarities[0],
    UnitOfMeasure: UnitOfMeasureList[0],
    EnglishUnitDetails: "",
    ArabicUnitDetails: "",
    Source: "",
    CustomFrequency: "",
    Code: "",
    Order: null,
    Baseline: "",
    CreatedBy: user.DisplayName,
    Created: new Date(),
    BaselineDate: new Date(),
    KPIType: null,
    ChampionModel: null,
    OwnerModel: null,
    ObjectiveOwnerModel: null,
    HasNoBaseline: false,
    HasNoTarget: false,
    StrategyType: props?.selectedType || strategyTypes[0],
    Strategy: props?.selectedStrategy || null,
    Theme: props?.selectedTheme || null,
    Pillar: props?.selectedPillar || null,
    Level:
      +props?.selectedType?.Value === 1
        ? props?.selectedLevel || objectiveLevels[0]
        : null,
    Objective: props.selectedSO || null,
    KPIMeasures: cleanKPIMeasures(2, new Date(), frequencies[0]),
    EnglishName: "",
    ArabicName: "",
    StartDate: new Date(),
    KPISectors: [],
    Years: "2",
    Frequency: frequencies[0],
    DataSource: findObjBYSource(kpiSources, "manual"),
    Attachments: [],
    CalculationMethod: calculationMethods[0],
    Parameters: [
      {
        ParameterName: "",
        Description: "",
      },
    ],
    Formula: [],
  });

  async function getKPIById(lists) {
    const { editData, modeSaveOrUpdate, isAddKpiProcess } = props;
    const requests = [];

    if (modeSaveOrUpdate === false) {
      requests.push(
        isAddKpiProcess
          ? api.processes.getRequestById(editData?.RequestID)
          : api.kpi.getById(editData?.ID)
      );
    }

    const [kpiRes] = await Promise.all(requests);

    if (kpiRes?.StatusCode === "Success") {
      let Data = {};

      if (isAddKpiProcess) Data = kpiRes?.Data?.Form;
      else Data = kpiRes?.Data;

      const isStrategic = Data?.Objective?.Theme ? true : false;
      const strategiesList = isStrategic
        ? lists.strategiesWithThemes
        : lists.strategiesWithPillars;
      const strategy = strategiesList?.find(
        (e) =>
          e.ID === Data?.Objective?.Theme?.Strategy?.ID ||
          e.ID === Data?.Objective?.Pillar?.Strategy?.ID
      );

      const theme = isStrategic
        ? strategy?.Themes?.find((e) => e.ID === Data?.Objective?.Theme?.ID)
        : null;

      const pillar = isStrategic
        ? null
        : strategy?.Pillars?.find((e) => e.ID === Data?.Objective?.Pillar?.ID);

      const sectors = Data?.KPISectors?.length
        ? Data?.KPISectors?.map((e) =>
            lists?.sectors?.find((item) => item.ID === e.ID)
          )
        : [];

      const KPISourceObj = findObjBYSource(kpiSources, Data.DataSource);
      const UnitOfMeasure = findUnitLabel(Data.UnitOfMeasure);
      const CalculationMethod = calculationMethods.find(
        (item) => parseInt(item.Value) === Data.CalculationMethod
      );
      setHasParameter(KPISourceObj?.Value === "semi-auto");
      setHasNoTarget(Data?.HasNoTarget);
      setHasNoBaseline(Data?.HasNoBaseline);
      setYearsValue(Data.Years);
      let formula = [];
      if (Data.Formula) {
        const regex = generateFormulaRegex(Data.Parameters);
        formula = Data.Formula.match(regex);
      }
      setState({
        ...Data,
        RequestID: editData?.RequestID || 0,
        Created: isAddKpiProcess ? new Date(kpiRes?.Data.Created) : new Date(),
        CreatedBy:
          kpiRes?.Data?.Form?.CreatedByModel?.DisplayName ||
          kpiRes?.Data?.Form?.CreatedBy ||
          user.DisplayName,
        StrategyType: strategyTypes.find(
          (item) => +item.Value === +strategy?.StrategyType
        ),
        Level: objectiveLevels.find(
          (item) => +item.Value === +Data.Objective.Level
        ),
        Strategy: strategy,
        Theme: theme,
        Pillar: pillar,
        DataSource: KPISourceObj,
        UnitOfMeasure,
        Polarity: polarities.find((item) => item.Value === Data.Polarity),
        StartDate: new Date(Data.StartDate),
        BaselineDate: new Date(Data.BaselineDate),
        Frequency: frequencies.find(
          (item) => item.Value?.toLowerCase() === Data.Frequency?.toLowerCase()
        ),
        KPISectors: sectors,
        Formula: formula,
        CalculationMethod,
        Parameters: Data?.Parameters || [
          {
            ParameterName: "",
            Description: "",
          },
        ],
        KPIMeasures: Data.KPIMeasures?.map((item, i) => ({
          ...item,
          ItemIndex: i,
          DueDate:
            Data?.Frequency === "custom"
              ? new Date(item?.DueDate)
              : moment(item.DueDate).locale("en").format("MM-DD-YYYY"),
          Year: new Date(item.DueDate).getFullYear(),
        })),
        CustomFrequency: Data.CustomFrequency || "",
      });
      setHistoryLog(kpiRes?.Data?.WFHistory);
      setApprovalSteps(kpiRes?.Data?.Steps);
    }
  }

  async function getLists() {
    const { modeSaveOrUpdate } = props;
    const { current } = formRef;
    const [
      typesRes,
      usersRes,
      strategiesWithThemesRes,
      strategiesWithPillarsRes,
      sectorsRes,
    ] = await Promise.all([
      api.kpiType.getAll(),
      api.user.getAll(),
      api.strategy.getAllWithObjectives({ strategyType: 1 }),
      api.strategy.getAllWithObjectives({ strategyType: 2 }),
      api.orgStructure.getAllTree(),
    ]);

    const newLists = {
      ...lists,
      kpiTypes: typesRes.StatusCode === "Success" ? typesRes.Data : [],
      users: usersRes.StatusCode === "Success" ? usersRes.Data : [],
      strategiesWithThemes:
        strategiesWithThemesRes.StatusCode === "Success"
          ? strategiesWithThemesRes.Data
          : [],
      strategiesWithPillars:
        strategiesWithPillarsRes.StatusCode === "Success"
          ? strategiesWithPillarsRes.Data
          : [],
      sectors: sectorsRes.StatusCode === "Success" ? sectorsRes.Data : [],
      loading: {
        ...lists.loading,
        users: false,
        kpiTypes: false,
        sectors: false,
        strategiesWithThemes: false,
        strategiesWithPillars: false,
      },
    };
    setLists(newLists);
    if (modeSaveOrUpdate && current) {
      current.setFieldValue("KPIType", typesRes.Data?.[0]);
      current.setFieldValue("OwnerModel", usersRes.Data?.[0]);
      current.setFieldValue("ObjectiveOwnerModel", usersRes.Data?.[1]);
    }

    return newLists;
  }

  async function fetchData() {
    const currentLists = await getLists();
    getKPIById(currentLists);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const { closeModal, isTaskCenter } = props;
  const { ID } = state;

  async function handleSubmit(values, formBag) {
    const { editData, closeModal, bindKPI, isAddKpiProcess } = props;
    const body = {
      ...values,
      StartDate: moment(values.StartDate).locale("en").format("MM-DD-YYYY"),
      BaselineDate: moment(values.BaselineDate)
        .locale("en")
        .format("MM-DD-YYYY"),
      DataSource: values.DataSource?.Value,
      Frequency: values.Frequency?.Value,
      Polarity: values.Polarity?.Value,
      CalculationMethod: values.CalculationMethod?.Value,
      UnitOfMeasure: values.UnitOfMeasure?.value,
      Formula: values.Formula?.join("") || undefined,
      IsDraft: isDraft,
      KPIMeasures:
        values.Frequency?.Value === "custom"
          ? values.KPIMeasures?.map((item, i) => ({
              ...item,
              DueDate: moment(item?.DueDate, "MM-DD-YYYY")
                .locale("en")
                .format("MM-DD-YYYY"),
            }))
          : values.KPIMeasures,
    };
    if (body.DataSource === "manual") {
      delete body.Parameters;
      delete body.Formula;
    }
    try {
      if (body.Attachments?.length) {
        const oldAttachments = [];
        const newAttachments = [];
        body.Attachments?.forEach((item) =>
          item.ID ? oldAttachments.push(item) : newAttachments.push(item)
        );
        if (newAttachments?.length) {
          const attachments = await api.attachment.create(newAttachments);
          body.Attachments = [
            ...oldAttachments,
            ...attachments?.Data?.map((item) => ({ ID: item })),
          ];
        }
      }
      const action = editData?.ID ? "update" : "create";
      let res;
      if (isAddKpiProcess) res = await api.processes.createKPI(body);
      else res = await api.kpi[action](body);
      if (res.StatusCode === "Success") {
        addToast.success(
          editData?.ID ? "toast.successUpdate" : "toast.successSave"
        );
        if (bindKPI) bindKPI(res.Data);
        closeModal();
        if (props?.getData) props?.getData();
      }
    } catch (error) {
      // addToast.error(error.message, {
      //   toastId: "get-users-error"
      // });
    } finally {
      formBag.setSubmitting(false);
    }
  }

  return (
    <div className="kpi-wizard-container">
      <Wizard
        ref={formRef}
        closeModal={closeModal}
        key={ID}
        initialValues={state}
        onSubmit={handleSubmit}
        isReadOnly={props?.isReadOnly}
        setIsDraft={setIsDraft}
        isAddKpiProcess={props?.isAddKpiProcess || false}
      >
        <Details
          isAddKpiProcess={props?.isAddKpiProcess || false}
          name={intl.formatMessage({ id: "kpi.kpi-details" })}
          validationSchema={detailsValidationSchema(isOrderValid, isCodeValid)}
          lists={lists}
          isEditMode={!!props.editData?.ID}
          isReadOnly={props?.isReadOnly}
          setHasParameter={setHasParameter}
          setHasNoBaseline={setHasNoBaseline}
          setHasNoTarget={setHasNoTarget}
          setIsOrderValid={setIsOrderValid}
          setIsCodeValid={setIsCodeValid}
          details={props.editData}
          state={state}
        />

        <Controllers
          name={intl.formatMessage({ id: "kpi.kpi-control" })}
          setHasParameter={setHasParameter}
          validationSchema={controlsValidationSchema(
            hasNoTarget || hasNoBaseline
          )}
          isEditMode={!!props.editData?.ID}
          yearsValue={yearsValue || 1}
          isReadOnly={props?.isReadOnly}
        />

        {hasParameter ? (
          <Formula
            validationSchema={formulaValidationSchema}
            name={intl.formatMessage({ id: "kpi.parameters" })}
            isEditMode={!!props.editData?.ID}
            isReadOnly={props?.isReadOnly}
          />
        ) : null}

        <Attachment
          name={intl.formatMessage({ id: "common.attachments" })}
          isReadOnly={props?.isReadOnly}
        />

        {isTaskCenter ? (
          <HistoryStep
            name={intl.formatMessage({ id: "initiative.history" })}
            isReadOnly={props?.isReadOnly}
            historyLog={historyLog}
            approvalSteps={approvalSteps}
            refetchData={() => {
              props.closeModal();
              if (props?.getData) props?.getData();
            }}
          />
        ) : null}
      </Wizard>
    </div>
  );
}

export default KPIWizard;
